import { getImageUrl } from './imageCompression';

export interface ImageDimensions {
  width: number;
  height: number;
}

export interface ResponsiveImageConfig {
  src: string;
  srcSet: string;
  sizes: string;
  placeholder?: string;
}

/**
 * Generate optimized image URLs for different screen sizes
 */
export function generateResponsiveImages(
  originalSrc: string,
  dimensions?: ImageDimensions
): ResponsiveImageConfig {
  // Handle Unsplash images
  if (originalSrc.includes('unsplash.com')) {
    return generateUnsplashResponsive(originalSrc);
  }

  // Handle Supabase storage images
  if (originalSrc.includes('supabase.co')) {
    return generateSupabaseResponsive(originalSrc, dimensions);
  }

  // Fallback for other images
  return {
    src: originalSrc,
    srcSet: '',
    sizes: '100vw'
  };
}

function generateUnsplashResponsive(src: string): ResponsiveImageConfig {
  const baseUrl = src.split('?')[0];
  
  const breakpoints = [400, 800, 1200, 1600, 2000];
  const srcSet = breakpoints
    .map(width => `${baseUrl}?w=${width}&auto=format&fit=crop&q=80 ${width}w`)
    .join(', ');

  return {
    src: `${baseUrl}?w=800&auto=format&fit=crop&q=80`,
    srcSet,
    sizes: '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
    placeholder: `${baseUrl}?w=20&auto=format&fit=crop&q=20&blur=10`
  };
}

function generateSupabaseResponsive(
  src: string,
  dimensions?: ImageDimensions
): ResponsiveImageConfig {
  if (!src.includes('/storage/v1/object/public/')) {
    return { src, srcSet: '', sizes: '100vw' };
  }

  // Check if this image has size variants (files ending with -size.ext)
  // Example: filename-large.jpg, filename-medium.jpg, etc.
  const hasVariants = src.match(/-(?:original|large|medium|small|thumbnail)\.(jpg|png|webp)$/i);

  if (!hasVariants) {
    // Image without variants - return as-is
    return { src, srcSet: '', sizes: '100vw' };
  }

  // Generate srcSet using our pre-generated size variants
  const sizes = [
    { size: 'thumbnail' as const, width: 400 },
    { size: 'small' as const, width: 800 },
    { size: 'medium' as const, width: 1200 },
    { size: 'large' as const, width: 1920 }
  ];

  const srcSet = sizes
    .map(({ size, width }) => `${getImageUrl(src, size)} ${width}w`)
    .join(', ');

  return {
    src: getImageUrl(src, 'large'),
    srcSet,
    sizes: '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw',
    placeholder: getImageUrl(src, 'thumbnail')
  };
}

/**
 * Generate a low-quality placeholder image
 */
export function generatePlaceholder(src: string): string {
  if (src.includes('unsplash.com')) {
    const baseUrl = src.split('?')[0];
    return `${baseUrl}?w=20&auto=format&fit=crop&q=20&blur=10`;
  }
  
  return src;
}

/**
 * Preload critical images
 */
export function preloadImage(src: string, priority = false): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    if (priority) {
      img.loading = 'eager';
      img.fetchPriority = 'high';
    }
    
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Batch preload multiple images
 */
export async function preloadImages(
  sources: string[], 
  options: { priority?: boolean; maxConcurrent?: number } = {}
): Promise<void> {
  const { priority = false, maxConcurrent = 3 } = options;
  
  // Process images in batches to avoid overwhelming the browser
  for (let i = 0; i < sources.length; i += maxConcurrent) {
    const batch = sources.slice(i, i + maxConcurrent);
    const promises = batch.map(src => 
      preloadImage(src, priority).catch(() => {
        console.warn(`Failed to preload: ${src}`);
      })
    );
    
    await Promise.allSettled(promises);
  }
}

/**
 * Get optimal image size based on container and device
 */
export function getOptimalImageSize(
  containerWidth: number,
  devicePixelRatio = window.devicePixelRatio || 1
): number {
  const targetWidth = containerWidth * devicePixelRatio;
  
  // Round up to nearest standard breakpoint
  const breakpoints = [400, 800, 1200, 1600, 2000];
  return breakpoints.find(bp => bp >= targetWidth) || 2000;
}

/**
 * Check if image format is supported
 */
export function supportsWebP(): boolean {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
}

export function supportsAVIF(): boolean {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL('image/avif').indexOf('data:image/avif') === 0;
}